import React, { useEffect, useState, Fragment } from "react";
import { path, pipe, prop, sortBy } from "ramda";
import { useSelector, useDispatch } from "react-redux";

import {
  CONTRIBUTOR_FIELD_BLURRED,
  CONTRIBUTOR_FIELD_CHANGED,
  CONTRIBUTOR_ROLE_CHANGED,
} from "basic-details/basicDetailsActions";

import {
  getContributorFields,
  getActiveStep,
  getStepErrors,
  getProjectContributorRoles,
} from "common/selectors";

import { firstError, hasError } from "common/validation";

import Grid from "@material-ui/core/Grid";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { makeStyles } from "@material-ui/core/styles";
import { isBlank, notBlank } from "common/general";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.dark,
    marginTop: theme.spacing(3),
  },

  subheading: {
    fontSize: ".85rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
}));

const CreatorDetailsForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const roles = useSelector(getProjectContributorRoles);
  const [roleOptions, setRoleOptions] = useState([]);
  useEffect(() => {
    console.log(roles);

    const options = pipe(sortBy(prop("name")))(roles);
    setRoleOptions(options);
  }, [roles]);

  const activeStep = useSelector(getActiveStep);
  const errors = useSelector(getStepErrors(activeStep));
  const fields = useSelector(getContributorFields);

  const dispatch = useDispatch();

  const onBlur = (field, value) => () =>
    dispatch({ type: CONTRIBUTOR_FIELD_BLURRED, field, value });
  const onRoleChange = (event, role) =>
    dispatch({ type: CONTRIBUTOR_ROLE_CHANGED, role });
  const onFieldChange = (path) => (event) => {
    const value = event.target.value;
    dispatch({ type: CONTRIBUTOR_FIELD_CHANGED, path, value });
  };

  return (
    <form autoComplete="none">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="contributor-type">
                  {t("creator_details_contributor_type")}
                </InputLabel>
                <Select
                  value={path(["type"], fields) || "PERSON"}
                  onChange={onFieldChange(["type"])}
                  inputProps={{
                    name: "contributor-type",
                    id: "contributor-type",
                  }}
                >
                  <MenuItem value="PERSON" key="PERSON">
                    {t("creator_details_contributor_type_person")}
                  </MenuItem>
                  <MenuItem value="CORPORATION" key="CORPORATION">
                    {t("creator_details_contributor_type_corporation")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {fields.type !== "CORPORATION" ? (
          <Fragment>
            <CustomizableTextField
              fieldOption="prefix"
              xs={6}
              sm={4}
              defaultLabel="Prefix"
              value={fields.prefix || ""}
              onChange={onFieldChange(["prefix"])}
              error={hasError(errors, "prefix")}
              helperText={t(firstError(errors, "prefix"))}
              onBlur={onBlur("prefix", fields.prefix)}
              inputProps={{ maxLength: 50 }}
            />

            <Grid item xs={12} sm={12}>
              <Grid container spacing={2}>
                <CustomizableTextField
                  fieldOption="first_name"
                  xs={12}
                  sm={4}
                  defaultLabel="First name"
                  value={fields.firstName || ""}
                  onChange={onFieldChange(["firstName"])}
                  error={hasError(errors, "firstName")}
                  helperText={t(firstError(errors, "firstName"))}
                  onBlur={onBlur("firstName", fields.firstName)}
                  inputProps={{ maxLength: 100 }}
                />

                <CustomizableTextField
                  fieldOption="middle_name"
                  xs={12}
                  sm={4}
                  defaultLabel="Middle Name"
                  value={fields.middleName || ""}
                  onChange={onFieldChange(["middleName"])}
                  error={hasError(errors, "middleName")}
                  helperText={t(firstError(errors, "middleName"))}
                  onBlur={onBlur("middleName", fields.middleName)}
                  inputProps={{ maxLength: 100 }}
                />

                <CustomizableTextField
                  fieldOption="last_name"
                  xs={12}
                  sm={4}
                  defaultLabel="Last name"
                  value={fields.lastName || ""}
                  onChange={onFieldChange(["lastName"])}
                  error={hasError(errors, "lastName")}
                  helperText={t(firstError(errors, "lastName"))}
                  onBlur={onBlur("lastName", fields.lastName)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>

            <CustomizableTextField
              fieldOption="suffix"
              xs={6}
              sm={4}
              defaultLabel="Suffix"
              value={fields.suffix || ""}
              onChange={onFieldChange(["suffix"])}
              error={hasError(errors, "suffix")}
              helperText={t(firstError(errors, "suffix"))}
              onBlur={onBlur("suffix", fields.suffix)}
              inputProps={{ maxLength: 100 }}
            />
          </Fragment>
        ) : (
          <Fragment>
            <CustomizableTextField
              fieldOption="corporation_name"
              xs={12}
              sm={12}
              defaultLabel="Corporation Name"
              value={fields.corporationName || ""}
              onChange={onFieldChange(["corporationName"])}
              error={hasError(errors, "corporationName")}
              helperText={t(firstError(errors, "corporationName"))}
              onBlur={onBlur("corporationName", fields.corporationName)}
              inputProps={{ maxLength: 400 }}
            />
          </Fragment>
        )}

        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              fieldOption="twitter_handle"
              xs={6}
              sm={6}
              defaultLabel="Twitter handle (optional)"
              value={fields.twitterHandle || ""}
              onChange={onFieldChange(["twitterHandle"])}
              error={hasError(errors, "twitterHandle")}
              helperText={t(firstError(errors, "twitterHandle"))}
              onBlur={onBlur("twitterHandle", fields.twitterHandle)}
              inputProps={{ maxLength: 30 }}
            />
            <CustomizableTextField
              fieldOption="website"
              xs={6}
              sm={6}
              defaultLabel="Website URL (optional)"
              value={fields.website || ""}
              onChange={onFieldChange(["website"])}
              error={hasError(errors, "website")}
              helperText={t(firstError(errors, "website"))}
              onBlur={onBlur("website", fields.website)}
              inputProps={{ maxLength: 2048 }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Grid container>
            <Grid item xs={12} sm={12}>
              <div className={classes.heading}>
                {t("creator_details_select_role")}
              </div>
              <div className={classes.subheading}>
                {t("creator_details_select_role_sub")}
              </div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                disableClearable
                autoSelect
                options={roleOptions}
                value={path(["role"], fields) || ""}
                getOptionLabel={(role) => (notBlank(role) ? role.display : "")}
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.name === val.name
                }
                onChange={onRoleChange}
                renderInput={(params) => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <CustomizableTextField
                      params={params}
                      fieldOption="role"
                      onBlur={onBlur("role", path(["role"], fields))}
                      error={hasError(errors, "role")}
                      helperText={t(firstError(errors, "role"))}
                      name={"role-" + Math.random()}
                      defaultLabel="Role"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default CreatorDetailsForm;
